<template>
  <FormMainContainer
    :long-content="true"
    :title="flowTitle"
    :previous-step-route="previousStepRoute"
  >
    <template
      v-if="isAdviserSelectionFlow && usedDepartmentAdvisersEnabled && !adviserRoleSlug"
      #title
    >
      <TitleSection :title="flowTitle" :previous-step-route="previousStepRoute">
        <template #leftNav>
          <AdviserRoleToggle />
        </template>
      </TitleSection>
    </template>
    <AppointmentQuestions :selected-flow="selectedFlow" @goToConfirmation="goToConfirmation" />
  </FormMainContainer>
</template>

<script lang="ts">
import { defineAsyncComponent, defineComponent, PropType } from 'vue'
import FormMainContainer from '~concierge/components/FormMainContainer.vue'
import HeaderSection from '~concierge/components/HeaderSection.vue'
import FooterSection from '~concierge/components/FooterSection.vue'
import AppointmentQuestions from '~concierge/components/AppointmentQuestionComponents/AppointmentQuestions.vue'
import { ConciergeAppointmentQuestionFlow } from '~types/concierge-enum'
import { router, usePage } from '@inertiajs/vue3'
import { trans, wTrans } from 'laravel-vue-i18n'
import TitleSection from '~concierge/components/TitleSection.vue'
import { useIdentityStore } from '~conciergeUiCommons/store/identityStore'
import { IdentityData } from '~concierge/types/IdentityTypes'
import { useAppointmentStore } from '~conciergeUiCommons/store/appointmentStore'

export default defineComponent({
  name: 'AppointmentFormPage',
  components: {
    HeaderSection,
    FooterSection,
    AppointmentQuestions,
    FormMainContainer,
    TitleSection,
    AdviserRoleToggle: defineAsyncComponent(
      () => import('~conciergeUiCommons/components/AdviserRoleToggle.vue')
    )
  },
  setup() {
    const identityStore = useIdentityStore()
    const appointmentStore = useAppointmentStore()
    const adviserRoleSlug = (usePage().props.adviserRoleSlug as string) || ''
    return {
      identityStore,
      appointmentStore,
      adviserRoleSlug
    }
  },
  props: {
    selectedFlow: {
      type: String as PropType<ConciergeAppointmentQuestionFlow>,
      required: true
    },
    previousStepRoute: {
      type: String,
      required: true
    },
    confirmationRoute: {
      type: String,
      required: true
    }
  },
  computed: {
    identity(): IdentityData | null {
      return this.identityStore.identity as IdentityData
    },
    usedDepartmentAdvisersEnabled(): boolean {
      return this.identity?.settings?.usedDepartmentAdvisersEnabled ?? false
    },
    flowTitle(): string {
      switch (this.selectedFlow) {
        case 'TestDriveAppointment':
          return trans('appointment_form_page.titles.test_drive_appointment')
        case 'FreeAppointment':
          return trans('appointment_form_page.titles.free_appointment')
        case 'AdviserSelectionAppointment':
          return trans('appointment_form_page.titles.adviser_selection_appointment')
        default:
          return ''
      }
    },
    isAdviserSelectionFlow(): boolean {
      return this.selectedFlow === 'AdviserSelectionAppointment'
    }
  },
  methods: {
    goToConfirmation(): void {
      if (this.confirmationRoute.length > 0) {
        router.visit(this.confirmationRoute)
      }
    }
  }
})
</script>
<style scoped>
.form-check-input,
.form-check-label {
  cursor: pointer;
}
.form-check-input:focus,
.form-check-input:active,
.form-check-input:checked {
  background-color: #ffffff;
  border-color: #ffffff;
  filter: unset;
}
.form-check-input:focus-visible {
  box-shadow: none;
}
.form-switch .form-check-input,
.form-switch .form-check-input:focus {
  --bs-form-switch-bg: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='inherit'/%3e%3c/svg%3e");
}
</style>
